/**
 * Normalizes `getCurrentYearReports` responses (either wrapped in `data` or raw).
 */
export function normalizeCurrentYearReportResponse(
  raw: unknown
): Record<string, unknown> | undefined {
  if (raw == null) return undefined;
  const r = raw as { data?: Record<string, unknown> };
  if (r.data && typeof r.data === "object" && !Array.isArray(r.data)) {
    return r.data as Record<string, unknown>;
  }
  return raw as Record<string, unknown>;
}

export type YearCertificateAggregate = {
  /** Annual pension contribution total for the certificate table */
  pensionTotal: number;
  /** Best-effort headcount for that year (max from monthly rows, or summary field) */
  employeeCount: number | null;
};

/**
 * Derives yearly pension totals and employee counts from a current-year report payload.
 * Aligns with contribution history: prefers `COM` rows; excludes `PEN` when typing exists.
 */
export function aggregateYearForCertificate(
  report: Record<string, unknown> | undefined
): YearCertificateAggregate {
  if (!report) return { pensionTotal: 0, employeeCount: null };

  const mb = report.monthlyBreakdown;

  if (mb && typeof mb === "object" && !Array.isArray(mb)) {
    const o = mb as Record<string, unknown>;
    const total =
      typeof o.totalContribution === "number" ? o.totalContribution : 0;
    // Only show headcount when this year has recorded contributions; otherwise
    // summary fields can mirror current employer stats and disagree with the PDF.
    const emp =
      total > 0
        ? typeof o.employeeCount === "number"
          ? o.employeeCount
          : typeof o.avgMonthlyEmployees === "number"
            ? Math.round(o.avgMonthlyEmployees)
            : null
        : null;
    return { pensionTotal: total, employeeCount: emp };
  }

  if (!Array.isArray(mb)) return { pensionTotal: 0, employeeCount: null };

  let comTotal = 0;
  let fallbackTotal = 0;
  let maxEmployees = 0;
  let sawEmployee = false;

  for (const row of mb) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const type = r.contributionType;
    const amt = r.totalContribution;
    if (typeof amt !== "number") continue;

    if (type === "PEN") continue;

    fallbackTotal += amt;

    if (type === "COM" || type == null || type === "") {
      comTotal += amt;
      const ec = r.employeeCount;
      if (typeof ec === "number") {
        sawEmployee = true;
        maxEmployees = Math.max(maxEmployees, ec);
      }
    }
  }

  const pensionTotal = comTotal > 0 ? comTotal : fallbackTotal;

  return {
    pensionTotal,
    employeeCount:
      pensionTotal > 0 && sawEmployee ? maxEmployees : null,
  };
}

export function parseSumAssured(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === "number" && !Number.isNaN(raw)) return raw;
  if (typeof raw === "string") {
    const n = parseFloat(raw.replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
